import * as THREE from 'three';
import { checkFootprintClash, createFootprintGeometry } from './footprintUtils';
export function interpolateBSpline(points, segments = 20) {
    if (points.length < 2) {
        throw new Error('At least 2 points are required for interpolation');
    }
    const result = [];
    const n = points.length - 1;
    // Helper function to interpolate between two points with given tangents
    const interpolateSegment = (p0, p1, t0x, t0y, t1x, t1y, t) => {
        const t2 = t * t;
        const t3 = t2 * t;
        // Hermite basis functions
        const h00 = 2 * t3 - 3 * t2 + 1;
        const h10 = t3 - 2 * t2 + t;
        const h01 = -2 * t3 + 3 * t2;
        const h11 = t3 - t2;
        return {
            x: h00 * p0.x + h10 * t0x + h01 * p1.x + h11 * t1x,
            y: h00 * p0.y + h10 * t0y + h01 * p1.y + h11 * t1y
        };
    };
    // Add first point
    result.push(points[0]);
    // Interpolate each segment
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        // Calculate tangent vectors
        let t0x, t0y, t1x, t1y;
        if (i === 0) {
            // First segment - use forward difference for start tangent
            t0x = (p1.x - p0.x) * 0.5;
            t0y = (p1.y - p0.y) * 0.5;
        }
        else {
            // Use central difference for start tangent
            t0x = (p1.x - points[i - 1].x) * 0.5;
            t0y = (p1.y - points[i - 1].y) * 0.5;
        }
        if (i === points.length - 2) {
            // Last segment - use backward difference for end tangent
            t1x = (p1.x - p0.x) * 0.5;
            t1y = (p1.y - p0.y) * 0.5;
        }
        else {
            // Use central difference for end tangent
            t1x = (points[i + 2].x - p0.x) * 0.5;
            t1y = (points[i + 2].y - p0.y) * 0.5;
        }
        // Generate points along the segment
        const steps = Math.max(2, Math.floor(segments / (points.length - 1)));
        for (let j = 1; j < steps; j++) {
            const t = j / steps;
            const pt = interpolateSegment(p0, p1, t0x, t0y, t1x, t1y, t);
            result.push(pt);
        }
        // Ensure we add the control point exactly
        if (i < points.length - 2) {
            result.push(p1);
        }
    }
    // Add last point
    result.push(points[n]);
    return result;
}
export const SCALE_FACTOR = 0.01; // Convert mm to Three.js units (1 unit = 100mm)
export const MAX_DIMENSION = 250; // Maximum dimension in mm
export const TARGET_FACE_SIZE = 3; // Target face size in mm 
export function generateBottomCapGeometry(geometry, extrusionHeight = 10) {
    // Get the vertices from the main geometry
    const positions = geometry.attributes.position.array;
    const bottomVertices = [];
    const EPSILON = 0.001;
    // Find all vertices at y=0 (or very close to it), in the order they appear in the geometry
    for (let i = 0; i < positions.length; i += 3) {
        if (Math.abs(positions[i + 1]) <= EPSILON) { // Check y coordinate
            bottomVertices.push(new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]));
        }
    }
    // DO NOT sort the vertices. Use them in the order found in the geometry.
    // Create a custom geometry for the bottom cap
    const bottomCapGeometry = new THREE.BufferGeometry();
    const vertices = [];
    const indices = [];
    // Add center vertex at y=0
    vertices.push(0, 0, 0);
    // Add center vertex at y=-extrusionHeight
    vertices.push(0, -extrusionHeight, 0);
    // Add all bottom vertices at y=0 and y=-extrusionHeight
    bottomVertices.forEach(vertex => {
        // Add vertex at y=0
        vertices.push(vertex.x, vertex.y, vertex.z);
        // Add vertex at y=-extrusionHeight
        vertices.push(vertex.x, -extrusionHeight, vertex.z);
    });
    // Create triangles for the top face (y=0)
    for (let i = 0; i < bottomVertices.length; i++) {
        indices.push(0, // center vertex at y=0
        i * 2 + 2, ((i + 1) % bottomVertices.length) * 2 + 2);
    }
    // Create triangles for the bottom face (y=-extrusionHeight)
    for (let i = 0; i < bottomVertices.length; i++) {
        indices.push(1, // center vertex at y=-extrusionHeight
        ((i + 1) % bottomVertices.length) * 2 + 3, i * 2 + 3);
    }
    // Create triangles for the sides
    for (let i = 0; i < bottomVertices.length; i++) {
        const nextI = (i + 1) % bottomVertices.length;
        // First triangle of the side face
        indices.push(i * 2 + 2, // top vertex
        nextI * 2 + 2, // top vertex of next
        i * 2 + 3 // bottom vertex
        );
        // Second triangle of the side face
        indices.push(nextI * 2 + 2, // top vertex of next
        nextI * 2 + 3, // bottom vertex of next
        i * 2 + 3 // bottom vertex
        );
    }
    // Set attributes
    bottomCapGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    bottomCapGeometry.setIndex(indices);
    bottomCapGeometry.computeVertexNormals();
    return bottomCapGeometry;
}
/**
 * Shared geometry validation for lamp designs.
 * @param geometry - The lamp geometry (THREE.BufferGeometry)
 * @param options - { overhangThreshold: number (degrees), height: number }
 * @returns { hasFootprintClash: boolean, hasOverhang: boolean, hasPrintableAreaClash: boolean }
 */
export function validateLampGeometry(geometry, options) {
    const { overhangThreshold, height } = options;
    // 1. Footprint clash
    const footprintGeometry = createFootprintGeometry(30, height);
    const hasFootprintClash = checkFootprintClash(geometry, footprintGeometry);
    // 2. Overhang
    const positions = geometry.attributes.position.array;
    const normals = geometry.attributes.normal.array;
    let hasOverhang = false;
    const EPSILON = 0.001;
    const thresholdRadians = overhangThreshold * Math.PI / 180;
    for (let i = 0; i < positions.length; i += 3) {
        const normal = { x: normals[i], y: normals[i + 1], z: normals[i + 2] };
        const position = { x: positions[i], y: positions[i + 1], z: positions[i + 2] };
        if (position.y <= EPSILON)
            continue;
        const norm = Math.sqrt(normal.x * normal.x + normal.y * normal.y + normal.z * normal.z);
        const normalizedNormal = {
            x: normal.x / norm,
            y: normal.y / norm,
            z: normal.z / norm
        };
        const dotProduct = Math.abs(normalizedNormal.y);
        const angle = Math.acos(dotProduct);
        const overhangAngle = (Math.PI / 2) - angle;
        if (overhangAngle > thresholdRadians) {
            hasOverhang = true;
            break;
        }
    }
    // 3. Printable area clash
    let hasPrintableAreaClash = false;
    for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const y = positions[i + 1];
        const z = positions[i + 2];
        if (Math.abs(x) > 125 || y > 250 || Math.abs(z) > 125) {
            hasPrintableAreaClash = true;
            break;
        }
    }
    return { hasFootprintClash, hasOverhang, hasPrintableAreaClash };
}
